import { useMemo } from 'react';

// Flood-zone bucketing mirrored from functions/utils/floodZoneUtils.js. Keep
// in sync — arvAgent's filterFloodZone treats zones in the same bucket as
// interchangeable, so the same "fundamentally different = different bucket"
// definition powers the card field's visibility.
const FLOOD_BUCKETS = {
    low: new Set(["X", "D", "B", "C", "LOW", "NONE", "NOTINFL", "OUT", "UNSPECIFIED"]),
    low_moderate: new Set(["XSHADED"]),
    moderate_low: new Set(["AH", "AO"]),
    moderate_high: new Set(["A", "AE", "AN", "A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "A9", "A99", "AR"]),
    high: new Set(["V", "VE", "V1", "V2", "V3", "V4", "V5", "V6", "V7", "V8", "V9", "COASTAL", "HIGH"])
};

const normalizeZoneCode = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return `A${value}`;
    if (typeof value !== 'string') return null;
    const cleaned = value.trim().toUpperCase().replace(/\s+/g, '');
    if (!cleaned) return null;
    if (/^A\d+$/.test(cleaned)) return 'A';
    if (/^V\d+$/.test(cleaned)) return 'V';
    if (cleaned === 'X(SHADED)' || cleaned === 'X-SHADED' || cleaned === 'X500') return 'XSHADED';
    return cleaned;
};

const ratingByCode = (code) => {
    if (!code) return null;
    if (FLOOD_BUCKETS.high.has(code)) return 'high';
    if (FLOOD_BUCKETS.moderate_high.has(code)) return 'moderate_high';
    if (FLOOD_BUCKETS.moderate_low.has(code)) return 'moderate_low';
    if (FLOOD_BUCKETS.low_moderate.has(code)) return 'low_moderate';
    if (FLOOD_BUCKETS.low.has(code)) return 'low';
    return null;
};

const floodZoneBucketOf = (property) => {
    const raw = property?.propertyAttributes?.floodZoneType;
    const first = Array.isArray(raw) ? raw[0] : raw;
    return ratingByCode(normalizeZoneCode(first));
};

// OR'd because upstream comp data can disagree: the backend treats
// belowGradeFinishedArea > 0 as definitive proof of a basement (see the
// streetView / needForExpansion / compPrep overrides that force basement=true
// from sqft), and the boolean catches markets where sqft isn't recorded but
// the supplier flagged the comp as basement. Gating on either alone would
// silently hide the row in half the data shapes.
const compHasBasement = (property) => {
    const attrs = property?.propertyAttributes;
    if (!attrs) return false;
    if (attrs.basement === true) return true;
    return typeof attrs.belowGradeFinishedArea === 'number' && attrs.belowGradeFinishedArea > 0;
};

// Returns boolean visibility flags for PropertyCard fields whose relevance
// depends on the subject + comp set as a whole. Add new flags here as more
// fields become conditional (e.g. waterfront, septic).
//
// Subject basement presence is passed explicitly because there's no single
// reliable field for it: in VerifyProperty/RunCompsV2 a user can flag the
// subject as having a basement WITHOUT recording sqft (Firestore
// `data.basement === true` / `data.basementAnalysis?.subjectBasement`), and
// the AWS subject rarely carries `belowGradeFinishedArea`. The caller is best
// positioned to combine those signals — see RunCompsV2.js / RunCompsView.js.
export default function usePropertyCardFieldFlags({ subjectProperty, comps, subjectBasementSqFootage, subjectHasBasement } = {}) {
    return useMemo(() => {
        const compsArr = Array.isArray(comps) ? comps : [];

        // Subject basement is decided strictly from explicit signals. AWS
        // `belowGradeFinishedArea` is unreliable for the subject lead (see the
        // belowGradeFinishedArea memo in PropertyCard) — the caller is
        // responsible for OR'ing it in if the deployment trusts it.
        const subjectBasement =
            subjectHasBasement === true
            || (typeof subjectBasementSqFootage === 'number' && subjectBasementSqFootage > 0);
        const showBasement = subjectBasement || compsArr.some(compHasBasement);

        // Flood zone: show only when at least two comps fall in DIFFERENT risk
        // buckets per arvAgent's filterFloodZone — that's the threshold for
        // "fundamentally different" zones in the underwriting pipeline. Subject
        // is intentionally excluded from this check; the row is only useful as
        // a comp-vs-comp comparison signal. Buckets where ratingByCode returns
        // null (unknown code) are ignored so garbage data doesn't trigger.
        const compBuckets = new Set();
        for (const c of compsArr) {
            const bucket = floodZoneBucketOf(c);
            if (bucket) compBuckets.add(bucket);
        }
        const showFloodZone = compBuckets.size >= 2;

        return { showBasement, showFloodZone };
    }, [comps, subjectBasementSqFootage, subjectHasBasement]);
}
